import { UpdateTaskRequest } from "@9amhealth/openapi";
import type { ModalHandle } from "@9amhealth/shared";
import styled from "@emotion/styled";
import { Blac } from "blac-next";
import type { FC, RefObject } from "react";
import React, { useEffect, useId, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BEHAVIORAL_HEALTH_QUESTIONNAIRE_GAD7,
  BEHAVIORAL_HEALTH_QUESTIONNAIRE_HOW_DIFFICULT_QUESTION_ID,
  BEHAVIORAL_HEALTH_QUESTIONNAIRE_PHQ8
} from "src/constants/misc";
import createTrackEvent from "src/lib/createTrackEvent";
import translate from "src/lib/translate";
import { KnownProgram } from "src/constants/programs";
import {
  CustomQuestionnaireAnswer,
  CustomQuestionnaireChoice
} from "src/state/QuestionnaireCubit/QuestionnaireState";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";
import { tracker, userState } from "src/state/state";
import CustomSuspense from "../../CustomSuspense/CustomSuspense";
import InDialog from "../../InDialog/InDialog";
import { BehavioralHealthDialogAssessment } from "./behavioralHealthDialogTools/BehavioralHealthDialogAssessment";
import { getBehavioralAssessment } from "./behavioralHealthDialogTools/getBehavioralAssessment";
import { BehavioralAssessment } from "./behavioralHealthDialogTools/BehavioralAssessment";

export const CenterQuestionnaireContent = styled.div`
  label: CenterQuestionnaireContent;
  display: grid;
  place-items: center;
  min-height: 100%;
  width: 100%;
  padding: 2rem 1rem 5rem !important;
  margin-bottom: calc(var(--keyboard-height, 0px) + 5rem);

  > div {
    width: 100%;
  }
`;

const AsyncCustomQuestionnaire = React.lazy(
  async () => import("../../CustomQuestionnaire/CustomQuestionnaire")
);

type CurrentQuestionnaire = "gad" | "phq" | "completed";
const QUESTIONNAIRE_OVERRIDES: readonly CurrentQuestionnaire[] = [
  "completed"
] as const;

const InAppMultipleQuestionnairesDialog: FC<{
  returnUrl?: string;
}> = () => {
  const urlQuery = new URLSearchParams(useLocation().search);
  const questionnaireOverride = urlQuery.get("questionnaireOverride");
  const [currentQuestionnaire, setCurrentQuestionnaire] =
    useState<CurrentQuestionnaire>(
      QUESTIONNAIRE_OVERRIDES.includes(questionnaireOverride ?? "")
        ? (questionnaireOverride as CurrentQuestionnaire)
        : "phq"
    );

  const questionnaireInstanceId = useId();

  const [answers, setAnswers] = useState<{
    phqAnswers: CustomQuestionnaireAnswer[];
    gadAnswers: CustomQuestionnaireAnswer[];
  }>();

  const markTaskAsStarted = () => {
    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    void taskManagementState.updateTaskStatus(
      {
        program: KnownProgram.BEHAVIORAL_HEALTH_SCREENING,
        group: "amazon",
        slug: "bh-screening-questionnaires"
      },
      UpdateTaskRequest.status.IN_PROGRESS
    );
  };
  const [overlayRef, setOverlayRef] = useState<RefObject<ModalHandle | null>>();
  const navigate = useNavigate();

  const handleClose = () => {
    if (overlayRef?.current) {
      overlayRef.current.dismiss();
    }
    navigate("/app");
  };

  useEffect(() => {
    // questionnaireOverride is used to jump at the end of the questionnaire,
    // in this case we don't want to update the status of the task as it's already started'
    if (questionnaireOverride) {
      return;
    }
    markTaskAsStarted();
    tracker.track(createTrackEvent("Behavioral Health Questionnaire Opened"));
  }, [questionnaireOverride]);

  useEffect(() => {
    const fn = async () => {
      const { phqAnswers, gadAnswers } =
        await userState.getBehavioralHealthQuestionnaireAnswers();
      setAnswers({ phqAnswers, gadAnswers });
    };

    void fn();
  }, [currentQuestionnaire]);

  const getScore = (answers?: CustomQuestionnaireAnswer[]) => {
    if (!answers) {
      return 0;
    }
    return Number(
      (
        answers.filter(
          (answer) => answer.questionId === "questionnaire-vars"
        )[0].fieldValue as CustomQuestionnaireChoice[]
      ).filter((choice) => choice.choiceId === "score")[0].value
    );
  };

  const score1 = getScore(answers?.phqAnswers);
  const score2 = getScore(answers?.gadAnswers);

  const howDifficultToWorkQuestionChoiceId =
    answers?.gadAnswers &&
    (
      answers.gadAnswers.filter(
        (x) =>
          x.questionId ===
          BEHAVIORAL_HEALTH_QUESTIONNAIRE_HOW_DIFFICULT_QUESTION_ID
      )[0]?.fieldValue as CustomQuestionnaireChoice
    )?.choiceId;

  const higherScore = Math.max(score1, score2);

  const assessment: BehavioralAssessment = getBehavioralAssessment({
    score: higherScore,
    lastQuestionChoiceId: howDifficultToWorkQuestionChoiceId
  });

  return (
    <InDialog
      popup
      onClose={handleClose}
      title={translate("prompt.behavior_questionnaire")}
      setRef={setOverlayRef}
      returnUrl={"/app"}
      backdropDismiss={false}
      height={"800px"}
      width={"700px"}
    >
      <CenterQuestionnaireContent>
        {currentQuestionnaire === "phq" && (
          <CustomSuspense>
            <AsyncCustomQuestionnaire
              autoSkip={true}
              resetScrollOnEachStep
              id={BEHAVIORAL_HEALTH_QUESTIONNAIRE_PHQ8}
              instanceId={questionnaireInstanceId}
              useRouting={false}
              onLastStepCompleted={() => {
                setCurrentQuestionnaire("gad");
              }}
            />
          </CustomSuspense>
        )}
        {currentQuestionnaire === "gad" && (
          <CustomSuspense>
            <AsyncCustomQuestionnaire
              autoSkip={true}
              resetScrollOnEachStep
              id={BEHAVIORAL_HEALTH_QUESTIONNAIRE_GAD7}
              instanceId={questionnaireInstanceId}
              useRouting={false}
              onLastStepCompleted={() => {
                setCurrentQuestionnaire("completed");
              }}
            />
          </CustomSuspense>
        )}
        {currentQuestionnaire === "completed" && (
          <BehavioralHealthDialogAssessment assessment={assessment} />
        )}
      </CenterQuestionnaireContent>
    </InDialog>
  );
};

export default InAppMultipleQuestionnairesDialog;
